/**
 * VerifyHelpDialog — onboarding modal shown on the public /verify-signature
 * page. On first visit it auto-opens (persisted via localStorage) so
 * non-technical users (drivers, customers, gate guards) understand the flow.
 * On repeat visits it's opened on-demand via the top-right help button.
 *
 * IMPORTANT UX rule: the user's PRIMARY intent is to see the verify result
 * (VALID / TIDAK VALID). We must NOT cover that result with an onboarding
 * modal. The parent page therefore passes `ready` — true once the verify
 * response has rendered — and we delay auto-open by a short beat AFTER
 * `ready` flips true, giving the user a chance to read the outcome first.
 *
 * Props:
 *   ready  boolean  — parent flips true once verify result is on screen.
 */
import React, { useEffect, useState } from "react";
import { QrCode, ShieldCheck, WarningOctagon, CheckCircle, Question, X } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const LS_KEY = "epool_verify_help_seen_v1";
const AUTO_OPEN_DELAY_MS = 2000;  // let user read the VALID/INVALID banner first

export default function VerifyHelpDialog({ ready = true }) {
  const [open, setOpen] = useState(false);

  // Auto-open once per browser, and only AFTER the verify result has
  // rendered — so the help modal doesn't obscure the "VALID / TIDAK VALID"
  // banner the user actually came here to read.
  useEffect(() => {
    if (!ready) return undefined;
    let seen = false;
    try {
      seen = !!localStorage.getItem(LS_KEY);
    } catch {
      /* localStorage disabled — treat as "not seen" but skip persistence */
    }
    if (seen) return undefined;
    const t = setTimeout(() => setOpen(true), AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, [ready]);

  const handleClose = (v) => {
    setOpen(v);
    if (!v) {
      try {
        localStorage.setItem(LS_KEY, "1");
      } catch {
        /* noop */
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="Cara verifikasi SPB"
          data-testid="verify-help-trigger"
          className="fixed top-4 right-4 md:top-6 md:right-6 z-40 w-11 h-11 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 active:scale-95 transition flex items-center justify-center"
        >
          <Question size={22} weight="bold" />
        </button>
      </DialogTrigger>

      <DialogContent
        className="max-w-lg rounded-xl p-0 gap-0 overflow-hidden"
        data-testid="verify-help-dialog"
      >
        <DialogHeader className="p-6 pb-4 bg-orange-50 border-b border-border">
          <DialogTitle className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <ShieldCheck size={22} weight="bold" />
            </span>
            <span className="text-xl font-black tracking-tight">
              Cara Verifikasi SPB Asli
            </span>
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2 ml-14">
            Panduan singkat untuk sopir, pelanggan &amp; petugas gudang.
          </p>
        </DialogHeader>

        <div className="p-6 space-y-5 max-h-[65vh] overflow-y-auto">
          <Step
            n={1}
            icon={<QrCode size={20} weight="bold" />}
            title="Cari QR di lembar SPB"
            body="QR Code Tanda Tangan terletak di bagian bawah SPB, dekat kolom tanda tangan admin. Pastikan QR tidak terpotong atau kotor."
          />

          <Step
            n={2}
            icon={<QrCode size={20} weight="bold" />}
            title="Scan pakai kamera HP"
            body="Buka kamera bawaan, Google Lens, atau scanner WA/LINE. Tidak perlu install aplikasi khusus. Halaman verifikasi akan terbuka otomatis di browser."
          />

          <Step
            n={3}
            icon={<CheckCircle size={20} weight="bold" className="text-emerald-600" />}
            title='Jika muncul "TANDA TANGAN VALID"'
            body="SPB dijamin asli. Cocokkan No SPB, No Polisi, produk & jumlah di layar dengan yang tercetak di kertas. Kalau semua cocok — muat/keluarkan barang."
            tone="valid"
          />

          <Step
            n={4}
            icon={<WarningOctagon size={20} weight="bold" className="text-destructive" />}
            title='Jika muncul "TIDAK VALID"'
            body="Jangan proses SPB ini. QR mungkin palsu, dicabut, atau kertas SPB sudah dimodifikasi. Segera lapor ke admin kantor untuk cross-check manual."
            tone="danger"
          />
        </div>

        <div className="p-4 border-t border-border bg-muted/30 flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Setiap scan gagal tercatat &amp; dilaporkan otomatis.
          </p>
          <Button
            onClick={() => handleClose(false)}
            className="rounded-xl"
            data-testid="verify-help-close"
          >
            Mengerti
            <X size={16} className="ml-1.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step({ n, icon, title, body, tone }) {
  const toneCls =
    tone === "valid"
      ? "border-emerald-200 bg-emerald-50/50"
      : tone === "danger"
      ? "border-destructive/30 bg-destructive/5"
      : "border-border";
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${toneCls}`}>
      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-black shrink-0">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 font-bold text-sm">
          {icon} {title}
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
